import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import whatsapp from './modules/whatsapp'

Vue.use(Vuex)

export default new Vuex.Store({
  modules: {
    whatsapp,
    auth: {
      namespaced: true,
      state: {
        token: localStorage.getItem('token') || '',
        user: JSON.parse(localStorage.getItem('user') || 'null')
      },
      getters: {
        token: state => state.token,
        user: state => state.user,
        isAuthenticated: state => !!state.token
      },
      mutations: {
        SET_TOKEN(state, token) {
          state.token = token
        },
        SET_USER(state, user) {
          state.user = user
        },
        CLEAR_AUTH(state) {
          state.token = ''
          state.user = null
        }
      }
    }
  },
  state: {
    token: localStorage.getItem('token') || '',
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    vehicles: [],
    vehiclesPagination: {
      count: 0,
      next: null,
      previous: null,
      currentPage: 1,
      pageSize: 10
    },
    userVehicles: [],
    userOffers: [],
    userDeposits: [],
    notifications: []
  },
  getters: {
    isAuthenticated: state => !!state.token,
    currentUser: state => state.user,
    vehicles: state => state.vehicles,
    vehiclesPagination: state => state.vehiclesPagination,
    vehiclesTotalPages: state => {
      return Math.ceil(state.vehiclesPagination.count / state.vehiclesPagination.pageSize)
    },
    userVehicles: state => state.userVehicles,
    userOffers: state => state.userOffers,
    userDeposits: state => state.userDeposits,
    hasActiveDeposit: state => {
      return state.userDeposits.some(deposit => deposit.status === 'active')
    },
    notifications: state => state.notifications,
    isBuyer: state => {
      // Staff tiene permisos de comprador y vendedor
      if (state.user && (state.user.is_staff || state.user.is_superuser)) {
        return true
      }
      return state.user && state.user.user_type === 'buyer'
    },
    isSeller: state => {
      // Staff tiene permisos de comprador y vendedor
      if (state.user && (state.user.is_staff || state.user.is_superuser)) {
        return true
      }
      return state.user && state.user.user_type === 'seller'
    },
    isStaff: state => {
      return state.user && (state.user.is_staff || state.user.is_superuser)
    }
  },
  mutations: {
    SET_TOKEN(state, token) {
      state.token = token
      // También actualizar el módulo auth
      this.commit('auth/SET_TOKEN', token)
    },
    SET_USER(state, user) {
      state.user = user
      // También actualizar el módulo auth
      this.commit('auth/SET_USER', user)
    },
    SET_VEHICLES(state, data) {
      state.vehicles = data.results || data
      if (data.count !== undefined) {
        state.vehiclesPagination.count = data.count
        state.vehiclesPagination.next = data.next
        state.vehiclesPagination.previous = data.previous
      }
    },
    SET_VEHICLES_PAGE(state, page) {
      state.vehiclesPagination.currentPage = page
    },
    SET_USER_VEHICLES(state, vehicles) {
      state.userVehicles = vehicles
    },
    SET_USER_OFFERS(state, offers) {
      state.userOffers = offers
    },
    SET_USER_DEPOSITS(state, deposits) {
      state.userDeposits = deposits
    },
    ADD_USER_DEPOSIT(state, deposit) {
      state.userDeposits.push(deposit)
    },
    UPDATE_USER_DEPOSIT(state, updatedDeposit) {
      const index = state.userDeposits.findIndex(d => d.id === updatedDeposit.id)
      if (index !== -1) {
        Vue.set(state.userDeposits, index, updatedDeposit)
      }
    },
    SET_NOTIFICATIONS(state, notifications) {
      state.notifications = notifications
    },
    CLEAR_AUTH(state) {
      state.token = ''
      state.user = null
      // También actualizar el módulo auth
      this.commit('auth/CLEAR_AUTH')
    }
  },
  actions: {
    // Authentication
    async login({ commit, dispatch }, credentials) {
      try {
        const response = await axios.post('/token/', credentials)
        const token = response.data.token
        localStorage.setItem('token', token)
        commit('SET_TOKEN', token)
        
        // Get user data
        await dispatch('fetchUserProfile')
        return { success: true }
      } catch (error) {
        return {
          success: false,
          message: error.response?.data?.detail || 'Error al iniciar sesión'
        }
      }
    },
    
    async loginWithGoogle({ commit, dispatch }, { google_token }) {
      try {
        const response = await axios.post('/auth/google/', { google_token })
        const token = response.data.token
        localStorage.setItem('token', token)
        commit('SET_TOKEN', token)
        
        // Get user data
        await dispatch('fetchUserProfile')
        
        return { 
          success: true,
          created: response.data.created || false,
          profile_complete: response.data.profile_complete || false,
          missing_fields: response.data.missing_fields || {}
        }
      } catch (error) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al iniciar sesión con Google'
        }
      }
    },
    
    async register({ dispatch }, userData) {
      try {
        await axios.post('/users/register/', userData)
        
        // Auto login after registration
        const credentials = {
          email: userData.email,
          password: userData.password
        }
        return dispatch('login', credentials)
      } catch (error) {
        return {
          success: false,
          message: error.response?.data || 'Error en el registro'
        }
      }
    },
    
    async fetchUserProfile({ commit, dispatch }) {
      try {
        console.log("Attempting to fetch user profile with token:", localStorage.getItem('token'));
        const response = await axios.get('/users/me/');
        console.log("User profile fetched successfully:", response.data);
        const user = response.data;
        localStorage.setItem('user', JSON.stringify(user));
        commit('SET_USER', user);
        return { success: true };
      } catch (error) {
        console.error("Failed to fetch user profile:", error.response || error);
        // If we get a 401 or 403, the token is likely invalid, so log out.
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          console.log("Token seems invalid. Logging out.");
          dispatch('logout');
        }
        return {
          success: false,
          message: 'Error al obtener datos del perfil'
        };
      }
    },
    
    async checkProfileCompletion() {
      try {
        const response = await axios.get('/users/complete-profile/')
        return {
          success: true,
          profile_complete: response.data.profile_complete,
          missing_fields: response.data.missing_fields,
          user: response.data.user
        }
      } catch (error) {
        console.error('Error checking profile completion:', error)
        return {
          success: false,
          message: error.response?.data?.error || 'Error al verificar el perfil'
        }
      }
    },
    
    async completeProfile({ commit }, profileData) {
      try {
        const response = await axios.patch('/users/complete-profile/', profileData)
        
        // Update user data in store
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user))
          commit('SET_USER', response.data.user)
        }
        
        return {
          success: true,
          profile_complete: response.data.profile_complete,
          missing_fields: response.data.missing_fields,
          message: response.data.message
        }
      } catch (error) {
        console.error('Error completing profile:', error)
        return {
          success: false,
          message: error.response?.data?.message || 'Error al completar el perfil',
          errors: error.response?.data || {}
        }
      }
    },
    
    logout({ commit }) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      commit('CLEAR_AUTH')
      // Optional: Reset router to home page
      window.location.pathname = '/'
    },
    
    // Vehicles
    async fetchVehicles({ commit }, page = 1) {
      try {
        const response = await axios.get(`/vehicles/?page=${page}`)
        commit('SET_VEHICLES', response.data)
        commit('SET_VEHICLES_PAGE', page)
        return { success: true, data: response.data }
      } catch (error) {
        return { 
          success: false,
          message: 'Error al cargar vehículos'
        }
      }
    },
    
    updateVehicleStatus(context, { id, status }) {
      return axios.patch(`/vehicles/${id}/`, { status });
    },

    deleteVehicle(context, vehicleId) {
      return axios.delete(`/vehicles/${vehicleId}/`);
    },

    async fetchUserVehicles({ commit }, page = 1) {
      try {
        const response = await axios.get(`/vehicles/?seller=me&page=${page}`);
        commit('SET_USER_VEHICLES', response.data)
        return { success: true }
      } catch (error) {
        return { 
          success: false,
          message: 'Error al cargar tus vehículos'
        }
      }
    },
    
    // Deposits (Señas)
    async fetchUserDeposits({ commit }) {
      try {
        const response = await axios.get('/deposits/')
        commit('SET_USER_DEPOSITS', response.data.results)
        return { success: true }
      } catch (error) {
        return { 
          success: false,
          message: 'Error al cargar señas'
        }
      }
    },
    
    async createDeposit({ commit }, depositData) {
      try {
        const response = await axios.post('/deposits/', depositData)
        commit('ADD_USER_DEPOSIT', response.data)
        return { 
          success: true,
          deposit: response.data
        }
      } catch (error) {
        return { 
          success: false,
          message: error.response?.data || 'Error al crear seña'
        }
      }
    },
    
    // Offers
    async fetchUserOffers({ commit }) {
      try {
        const response = await axios.get('/offers/')
        commit('SET_USER_OFFERS', response.data.results)
        return { success: true }
      } catch (error) {
        return { 
          success: false,
          message: 'Error al cargar ofertas'
        }
      }
    },
    
    async makeOffer({ dispatch }, offerData) {
      try {
        await axios.post('/offers/', offerData)
        // Refresh offers after creating new one
        dispatch('fetchUserOffers')
        dispatch('fetchUserDeposits') // También actualizamos los depósitos ya que cambia su estado
        return { success: true }
      } catch (error) {
        return { 
          success: false,
          message: error.response?.data || 'Error al crear oferta'
        }
      }
    },
    
    async respondToOffer({ dispatch }, { offerId, action, amount }) {
      try {
        let endpoint = `/offers/${offerId}/${action}/`
        let data = {}
        
        if (action === 'counter_offer') {
          data = { amount }
        }
        
        await axios.post(endpoint, data)
        // Refresh offers
        dispatch('fetchUserOffers')
        return { success: true }
      } catch (error) {
        return { 
          success: false,
          message: error.response?.data || `Error al ${action} oferta`
        }
      }
    },
    
    // Notifications
    async fetchNotifications({ commit }) {
      try {
        const response = await axios.get('/notifications/')
        commit('SET_NOTIFICATIONS', response.data.results)
        return { success: true }
      } catch (error) {
        return { 
          success: false,
          message: 'Error al cargar notificaciones'
        }
      }
    }
  }
})
